// Network sizing engine — PRODUCT-52.
// Splits bandwidth by traffic pattern, computes uplink tiers, VLAN count, and access ports.

import type { VMConfig, SizingConfig, NetworkResult } from '../../types';

// Standard uplink speeds in Mbps — north-south is rounded up to the next tier.
const UPLINK_TIERS_MBPS = [1_000, 10_000, 25_000, 40_000, 100_000];

// vMotion overhead applied to east-west bandwidth pool.
const VMOTION_OVERHEAD_FACTOR = 1.2;

// Storage ports per host (one dedicated storage NIC pair — host connects to SAN/NAS fabric).
const STORAGE_PORTS_PER_HOST = 2;

function vmAppliesToSite(vm: VMConfig, site: 'primary' | 'dr'): boolean {
  return vm.siteAssignment === site || vm.siteAssignment === 'both';
}

function roundUpToNextUplinkGbps(bandwidthMbps: number): number {
  for (const tierMbps of UPLINK_TIERS_MBPS) {
    if (bandwidthMbps <= tierMbps) {
      return tierMbps / 1_000;
    }
  }
  // Exceeds 100G — return the highest tier; note will be emitted separately.
  return UPLINK_TIERS_MBPS[UPLINK_TIERS_MBPS.length - 1] / 1_000;
}

/**
 * Run the network sizing engine for a given site.
 * Returns one NetworkResult covering all environments at the site.
 * hostCount is the total HA-adjusted host count from the compute engine.
 */
export function runNetworkSizing(
  vms: VMConfig[],
  _config: SizingConfig,
  site: 'primary' | 'dr',
  hostCount: number,
): NetworkResult {
  const notes: string[] = [];

  const siteVms = vms.filter(vm => vmAppliesToSite(vm, site));

  const totalVmCount = siteVms.reduce((sum, vm) => sum + vm.quantity, 0);
  notes.push(
    `Network sizing for ${site} site: ${totalVmCount} VM instance(s) across ${siteVms.length} type(s)`,
  );

  // ── Step 1: Total VM bandwidth ────────────────────────────────────────────────

  const totalVmBandwidthMbps = siteVms.reduce(
    (sum, vm) => sum + vm.networkBandwidthMbps * vm.quantity,
    0,
  );

  notes.push(`Total aggregate VM bandwidth: ${totalVmBandwidthMbps} Mbps`);

  // ── Step 2: Split by traffic pattern ─────────────────────────────────────────
  // East-west pool: VMs with trafficPattern = 'east_west' or 'mixed'
  // North-south pool: VMs with trafficPattern = 'north_south' or 'mixed'
  // Note: 'mixed' VMs contribute to both pools.

  const eastWestRaw = siteVms.reduce((sum, vm) => {
    if (vm.networkConfig.trafficPattern === 'east_west' || vm.networkConfig.trafficPattern === 'mixed') {
      return sum + vm.networkBandwidthMbps * vm.quantity;
    }
    return sum;
  }, 0);

  const northSouthRaw = siteVms.reduce((sum, vm) => {
    if (vm.networkConfig.trafficPattern === 'north_south' || vm.networkConfig.trafficPattern === 'mixed') {
      return sum + vm.networkBandwidthMbps * vm.quantity;
    }
    return sum;
  }, 0);

  notes.push(
    `Traffic split — east-west (pre-vMotion): ${eastWestRaw} Mbps, ` +
    `north-south: ${northSouthRaw} Mbps`,
  );

  // ── Step 3: Apply vMotion overhead to east-west ───────────────────────────────

  const eastWestBandwidthMbps = eastWestRaw * VMOTION_OVERHEAD_FACTOR;

  notes.push(
    `vMotion overhead applied to east-west (×${VMOTION_OVERHEAD_FACTOR}): ` +
    `${eastWestRaw} → ${eastWestBandwidthMbps} Mbps`,
  );

  // ── Step 4: Round north-south up to next standard uplink tier ─────────────────

  const recommendedUplinkGbps = roundUpToNextUplinkGbps(northSouthRaw);

  if (northSouthRaw > UPLINK_TIERS_MBPS[UPLINK_TIERS_MBPS.length - 1]) {
    notes.push(
      `WARNING: North-south bandwidth ${northSouthRaw} Mbps exceeds maximum standard ` +
      `uplink tier (100G). Multiple 100G uplinks or aggregated fabric ports required.`,
    );
  }

  notes.push(
    `North-south ${northSouthRaw} Mbps rounded up to next standard uplink: ${recommendedUplinkGbps} Gbps`,
  );

  // ── Step 5: Access port count ─────────────────────────────────────────────────
  // Hosts are dual-homed (2 ports each) + dedicated storage ports per host.

  const recommendedAccessPortCount = hostCount * 2 + hostCount * STORAGE_PORTS_PER_HOST;

  notes.push(
    `Access port count: ${hostCount} hosts × 2 (dual-homed NIC) + ` +
    `${hostCount} hosts × ${STORAGE_PORTS_PER_HOST} (storage ports) = ${recommendedAccessPortCount} ports`,
  );

  // ── Step 6: VLAN count — distinct security domains across all VMs ─────────────
  // Count distinct securityDomain values across the full project VM set (not just this site).
  // The VLAN topology is project-wide even if some VMs only run at one site.

  const distinctDomains = new Set(vms.map(vm => vm.networkConfig.securityDomain));
  const requiredVlanCount = distinctDomains.size;

  notes.push(
    `Required VLAN count: ${requiredVlanCount} distinct security domain(s) — ` +
    `[${[...distinctDomains].sort().join(', ')}]`,
  );

  // ── Step 7: Latency placement notes ──────────────────────────────────────────

  const lowLatencyVms = siteVms.filter(
    vm => vm.networkConfig.latencySensitivity === 'low_latency',
  );
  const ultraLowLatencyVms = siteVms.filter(
    vm => vm.networkConfig.latencySensitivity === 'ultra_low_latency',
  );

  if (lowLatencyVms.length > 0) {
    const vmNames = lowLatencyVms.map(vm => vm.name).join(', ');
    notes.push(
      `Low-latency VMs (${lowLatencyVms.length} type(s)): ${vmNames}. ` +
      `Place on lowest-latency switching tier; avoid routing hops within the cluster fabric.`,
    );
  }

  if (ultraLowLatencyVms.length > 0) {
    const vmNames = ultraLowLatencyVms.map(vm => vm.name).join(', ');
    notes.push(
      `WARNING: Ultra-low-latency VMs (${ultraLowLatencyVms.length} type(s)): ${vmNames}. ` +
      `These require RDMA-capable fabric (RoCEv2 or InfiniBand) or dedicated physical segments. ` +
      `Co-location requirements reduce effective cluster capacity — review placement policy.`,
    );
  }

  return {
    site,
    totalVmBandwidthMbps,
    eastWestBandwidthMbps,
    northSouthBandwidthMbps: northSouthRaw,
    recommendedUplinkGbps,
    recommendedAccessPortCount,
    requiredVlanCount,
    notes,
  };
}
